import dummy_1 from '@/../public/img/search/dummy_1.png';
import dummy_2 from '@/../public/img/search/dummy_2.png';
import dummy_3 from '@/../public/img/search/dummy_3.png';
import ApiError from '@/app/api/ApiError';
import {thresolds} from '@/cst/utils';
import crypto from 'crypto';
import {cloneDeep} from 'lodash';
import lzjs from 'lzjs';
import moment from 'moment';
import {Locale} from 'next-intl';
import {getLocale, getTranslations} from 'next-intl/server';
import {notFound} from 'next/navigation';
import {env} from 'process';
import Height from '../modules/Height';
import getLayout from './layouts';
import getModuleData from './modules';

export const dummyImages = [dummy_1, dummy_2, dummy_3];

export async function htmlParseOptions(
  content: string,
  locale: Locale,
  options: any
) {
  const moduleData = await getModuleData(content, locale, options);
  return {
    replace: (domNode: any) => {
      if (domNode.type === 'text') {
        const text = domNode.data;
        if (/\[module:(.+)\]/g.test(text)) {
          const match = text.match(/\[module:(.+)\]/);
          if (match && match[1]) {
            const moduleName = match[1].split('=')[0];
            const moduleItems = moduleData?.[match[1]] || [];
            const moduleOptionKey = match[1].split('=')[1] || '';
            const moduleOption = cloneDeep(options.meta?.[moduleOptionKey]);

            const moduleSlug = match[1].split('=')[1] || '';
            return getLayout({
              locale,
              moduleSlug,
              moduleName,
              moduleData: moduleItems,
              moduleOption,
              options
            });
          }
        }

        if (/\[h=(.+)\]/g.test(text)) {
          const match = text.match(/\[h=(.+)\]/);
          if (match && match[1]) {
            const moduleSlug = match[1] || '';
            return <Height {...{moduleSlug}} />;
          }
        }
      }
      return null;
    }
  };
}

export function replacePToDiv(content: string) {
  return content.replace(/<p([^>]*)>([\s\S]*?)<\/p>/g, '<div$1>$2</div>');
}

export function target(href: string) {
  const url = href.trim();
  if (url && url.startsWith('https://www.aswatson.com')) {
    return '_self';
  }
  if (url && url.startsWith('https://185-anniversary.aswatson.com')) {
    return '_self';
  }
  if (url && (url.startsWith('http') || url.startsWith('/uploads'))) {
    return '_blank';
  }
  return '_self';
}

export function title(...title: string[]) {
  return title
    .filter((t) => t !== '' || t !== null || t !== undefined)
    .join(' | ');
}

export function formHash(...h: string[]) {
  const md5 = crypto.createHash('md5');
  const secret = env.SECRET_KEY;
  return [md5.update(secret + h.join('')).digest('hex'), ...h].join('.');
}
export function genHash() {
  const random = Math.random().toString(36).substring(2, 15);
  const now = new Date().getTime();
  return formHash(random, now.toString(36));
}

const tokenCache = new Map();
export function validHash(hash: string, options?: any) {
  const hashArr = hash.split('.');
  const random = hashArr[1];
  const ttl = options?.ttl || 1000 * 60 * 60 * 24; // 1 day

  const expectedHash = formHash(random, hashArr[2] ?? '');
  if (options.human) {
    if (hash.split('.').slice(0, 3).join('.') != expectedHash) {
      return {valid: false, message: 'Invalid hash: format unmatch (h)'};
    }
    if (!validHuman(hash)) {
      return {valid: false, message: 'Invalid hash: human rule'};
    }
  } else {
    if (hash != expectedHash) {
      return {valid: false, message: 'Invalid hash: format unmatch'};
    }
  }

  // 1 hours
  const timestamp = parseInt(hashArr[2] ?? '', 36);
  if (new Date().getTime() - timestamp > ttl) {
    throw new ApiError('TOKEN_EXPIRED');
  }

  // check cached token
  const isOneTime = !options?.nonOneTime;
  if (tokenCache.get(hash) && isOneTime) {
    return {valid: false, message: 'Token used'};
  } else {
    // cache token
    tokenCache.set(hash, {hash, timestamp});
  }

  // delete expired token
  const now = new Date().getTime();
  tokenCache.forEach((v, k) => {
    if (now - v.timestamp > ttl) {
      tokenCache.delete(k);
    }
  });

  return {valid: true, message: 'Valid hash'};
}

export function validHuman(hash: string) {
  const oHash = hash.split('.').slice(0, 3).join('.');
  const encrypted = hash.replace(oHash + '.', '');
  try {
    const decipher = crypto.createDecipheriv(
      'aes-256-cbc',
      oHash.substring(0, 32),
      oHash.substring(0, 16)
    );
    const buffer = Buffer.from(encrypted, 'hex');
    let decrypted = decipher.update(buffer);
    decrypted = Buffer.concat([decrypted, decipher.final()]);
    const actions = JSON.parse(lzjs.decompress(decrypted.toString('utf8')));

    let valid = false;
    Object.entries(thresolds).map(([k, v]) => {
      if (!valid && actions?.[k]?.length >= v) {
        valid = true;
      }
    });
    return valid;
  } catch (e: any) {
    console.error(e, {hash});
    return false;
  }
}

export const formPreviewHash = (...h: string[]) => {
  const secret = process.env.PREVIEW_SECRET ?? 'preview';
  const str = h.join('') + secret;
  let hash = 5381;
  let i = str.length;
  while (i) {
    hash = (hash * 33) ^ str.charCodeAt(--i); // hash * 33 + charCode
  }
  return (hash >>> 0).toString(16); // convert to unsigned 32-bit integer and to hexadecimal
};

export async function validatePreview(k: string) {
  if (!k || k.split('.').length < 2) {
    notFound();
  }
  const hash = formPreviewHash(k.split('.')[0]);
  if (hash !== k.split('.')?.[1]) {
    notFound();
  }
}

export const formEditModeHash = (...h: string[]) => {
  const secret = process.env.STRAPI_ADMIN_EDIT_MODE_SECRET ?? 'default-secret';
  const str = h.join('') + secret;
  let hash = 5381;
  let i = str.length;
  while (i) {
    hash = (hash * 33) ^ str.charCodeAt(--i); // hash * 33 + charCode
  }
  return (hash >>> 0).toString(16); // convert to unsigned 32-bit integer and to hexadecimal
};

export function validateEditMode(k: string) {
  try {
    if (!k || k.split('.').length < 2) {
      return false;
    }
    const hash = formEditModeHash(k.split('.')[0]);
    if (hash !== k.split('.')?.[1]) {
      return false;
    }
    const d = parseInt(k.split('.')[0], 36);
    const t = moment().subtract(1, 'day').unix();
    if (d < t) {
      return false;
    }
    return true;
  } catch (e: any) {
    return false;
  }
}

export function abstract(
  content: string,
  locale: Locale,
  maxDescriptionLength: number
) {
  const words = content
    .replace(/\[module:[^\]]+\]/g, ' ')
    .replace(/nbsp;/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .split(' ')
    .filter((str) => str)
    .join(' ')
    .replace(/&amp;/g, '&')
    .replace(/[.,\/#!$%\^&\*:{}=\-_`~()]/g, ' ')
    .split(/\s+/);
  if (locale === 'en') {
    return (
      words.slice(0, maxDescriptionLength).join(' ') +
      (maxDescriptionLength < words.length ? '...' : '')
    );
  } else {
    return (
      words
        .map((w) => {
          return w;
        })
        .join('')
        .substring(0, maxDescriptionLength) +
      (maxDescriptionLength < content.length ? '...' : '')
    );
  }
}

export async function jsonLd(pageDetail: any, options: any = {}) {
  const jsonLd = [];
  try {
    const locale = await getLocale();
    const t = await getTranslations({locale, namespace: 'jsonLd'});
    const slugs = pageDetail?.slug?.split('/') ?? [];
    const url = env.APP_URL + (locale != 'en' ? '/' + locale : '');

    const post_meta = pageDetail.post_meta ?? {};
    const image = [
      ...(options?.images || []),
      ...((
        Object.values(post_meta).find((v: any) => {
          return v.type === 'note';
        }) as any
      )?.items
        .map((i: any) => (typeof i?.image == 'string' ? i.image : null))
        .filter(Boolean) ?? [])
    ]
      .map((i: any) => {
        return typeof i == 'string'
          ? (i.trim().startsWith('http') ? '' : env.APP_URL) + i.trim()
          : '';
      })
      .filter(Boolean);

    if (slugs[0] == 'home') {
      // OrganizationJsonLd
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        url,
        logo: env.APP_URL + '/img/asw-185-logo-' + locale + '.png',
        name: t('name'),
        description: t('description'),
        email: 'enquiry@aswatson.com',
        telephone: '+852 2606 8833',
        address: {
          '@type': 'PostalAddress',
          postalCode: '',
          streetAddress: t('streetAddress'),
          addressLocality: t('addressLocality'),
          addressCountry: t('addressCountry'),
          addressRegion: t('addressRegion')
        }
      });
    } else if (['news', 'sustainability'].includes(pageDetail.post_type)) {
      // newsJsonLd
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: pageDetail.title,
        image,
        datePublished: pageDetail.posted_at,
        dateModified: pageDetail.updatedAt
      });
    } else {
      // webJsonLd
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: url + '/' + pageDetail.slug
      });

      if (slugs.length > 0) {
        const breadcrumbJsonLd = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: slugs.map((slug: string, index: number) => {
            return {
              '@type': 'ListItem',
              position: index + 1,
              name: slug,
              item: url + '/' + slugs.slice(0, index + 1).join('/')
            };
          })
        };
        jsonLd.push(breadcrumbJsonLd);
      }
    }
  } catch (e: any) {
    console.error(e);
  }
  return jsonLd.length == 1 ? jsonLd[0] : jsonLd;
}

export async function callFetch(
  url: string,
  options: any = {},
  timeout = 30000
) {
  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
    console.warn('Fetch aborted due to timeout');
  }, timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error: any) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
}
